import { useMemo } from 'react';

import useSurveySettings from 'src/hooks/useSurveySettings';
import type {
  FilterGroups,
  FilterValueSpec,
} from 'src/components/app/context/FilterContext';
import { useSection } from 'src/components/app/context/SectionContext';
import { orgUnitToFilter } from 'src/components/common/OrgTree/helper';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import {
  dateRangeToFilter,
  defaultDateRange,
} from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

import { calculateInitialDateRange } from '../FilterColumn/DateFilter/helper';

import { FILTER_KEYS } from './constants';

const orgUnitFilterSpec: FilterValueSpec = {
  name: ORG_UNIT_FILTER_NAME,
  multiple: true,
  required: true,
  hideFilterChips: false,
};
const dateRangeFilterSpec: FilterValueSpec = {
  name: FILTER_KEYS.DATE_RANGE,
  multiple: false,
  required: true,
  hideFilterChips: true,
  independent: true,
};
const dateRangeComparisonFilterSpec: FilterValueSpec = {
  name: FILTER_KEYS.DATE_RANGE_COMPARISON,
  multiple: false,
  required: false,
  hideFilterChips: true,
  independent: true,
};
const blankFilterSpec: FilterValueSpec = {
  name: /.*/,
  required: false,
  multiple: true,
  hideFilterChips: false,
};

/**
 * What a page's comparison is against.
 * - `comparisonSurvey`: the survey configured as `SurveySettings.comparison_survey`. The backend routes the
 *   flagged request to that survey for every data source.
 * - `previousPeriod`: an earlier period of the same survey, enabled by the dashboard's `comparisonRangeFilter`
 *   row.
 */
export type ComparisonMode = 'comparisonSurvey' | 'previousPeriod';

interface InitialFilterOptions {
  dateRange?: boolean;
  comparison?: ComparisonMode;
}

export const useInitialFilterGroups = ({
  dateRange: hasDateRange = false,
  comparison,
}: InitialFilterOptions = {}) => {
  const { currentTimePeriodStartDate } = useSurveySettings();
  const { orgUnits } = useSection();
  const firstOrgUnit = useMemo(() => orgUnits?.slice(0, 1)[0], [orgUnits]);

  // hydrate orgUnit and dateRange
  const initialFilterGroups: FilterGroups = useMemo(() => {
    const initialDateRange = hasDateRange
      ? calculateInitialDateRange(defaultDateRange, currentTimePeriodStartDate)
      : undefined;

    return {
      [ORG_UNIT_FILTER_NAME]: firstOrgUnit
        ? [orgUnitToFilter(firstOrgUnit)]
        : [],
      ...(initialDateRange
        ? { [FILTER_KEYS.DATE_RANGE]: [dateRangeToFilter(initialDateRange)] }
        : {}),
    };
  }, [firstOrgUnit, hasDateRange, currentTimePeriodStartDate]);

  // tell the filter how to use orgunit, dateRange and dateRangeComparison filters
  const initialFilterSpecs = useMemo(
    () => [
      orgUnitFilterSpec,
      ...(hasDateRange ? [dateRangeFilterSpec] : []),
      ...(comparison ? [dateRangeComparisonFilterSpec] : []),
      blankFilterSpec,
    ],
    [hasDateRange, comparison],
  );

  return {
    initialFilterGroups,
    initialFilterSpecs,
  };
};

export default useInitialFilterGroups;
